import React, { useState } from 'react';
import { validateFiles } from '@askporter/utils/src/utils/fileUploader/validateFiles';
import { ImageList, ImageListItem, Theme, useMediaQuery } from '@mui/material';
import { FilePath } from '@askporter/client-grieg-lyric';
import { UploaderFileTypes } from '@askporter/utils';
import { DragAndDropUploaderBox } from '../components/DragAndDropUploaderBox';
import { FilePreview } from '../components/FilePreview';
import { UploaderImageItem } from '../components/UploaderImageItem';
import { ImageUploaderProps } from '../types';

export const ImageUploader = ({
  idPrefix,
  t,
  value,
  helperText,
  accept = [UploaderFileTypes.PNG, UploaderFileTypes.JPG, UploaderFileTypes.JPEG],
  width,
  height,
  rowHeight,
  maxBytes,
  maxFiles,
  imageValidation,
  onInvalidFilesDrop,
  uploaderDisabled = false,
  canPreview = true,
  deletionEnabled = true,
  deleteButtonVisible = true,
  onValidFilesDrop,
  onDelete,
  required,
  requiredFileError = false,
  removeUploaderBox = false,
}: ImageUploaderProps): JSX.Element => {
  const isSmallDevice = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const [filePreview, setFilePreview] = useState<FilePath | undefined>();

  return (
    <>
      <ImageList
        sx={{
          width,
          height,
          margin: 0,
        }}
        cols={2}
        rowHeight={rowHeight}
        gap={8}
      >
        {!removeUploaderBox && (
          <ImageListItem cols={1} rows={1} sx={{ margin: '2px' }}>
            <DragAndDropUploaderBox
              t={t}
              idPrefix={idPrefix}
              accept={accept}
              helperText={helperText}
              multiple={maxFiles > 1}
              onDrop={async (files) => {
                const validatedFiles = await validateFiles({
                  files,
                  accept,
                  maxSize: maxBytes,
                  maxFiles,
                  imageValidation,
                  onInvalidFilesDrop,
                  existingFilesCount: value.length,
                });
                if (validatedFiles.length > 0) {
                  onValidFilesDrop(validatedFiles);
                }
              }}
              disabled={uploaderDisabled}
              required={required}
              requiredFileError={requiredFileError}
            />
          </ImageListItem>
        )}
        {value.map((file, _id) => (
          <UploaderImageItem
            key={_id}
            idPrefix={`${idPrefix}-uploader-list`}
            removeBtnDisabled={!deletionEnabled}
            isRemoveFileBtnVisible={deleteButtonVisible}
            t={t}
            file={{
              ...file,
              state: file.state || 'success',
              handleRemove: () => {
                onDelete(file);
              },
              setFilePreview: () => {
                setFilePreview({
                  fileUrn: file.file.fileRef,
                  filePath: file.path || file.tempPath,
                });
              },
            }}
            isClickToPreviewAllowed={canPreview}
          />
        ))}
      </ImageList>

      {filePreview && (
        <FilePreview
          t={t}
          isSmallDevice={isSmallDevice}
          file={filePreview}
          handleClose={() => setFilePreview(undefined)}
        />
      )}
    </>
  );
};
